import configuration from '@assets/video-compression-presets.json';
import type { VideoCompressionPreset, VideoCompressionPresetId } from '@interfaces/video-compression.interfaces';

export function normalizeVideoCompressionPresetId(value: unknown): VideoCompressionPresetId | null {
  switch (value) {
    case 'veryLow':
    case 'low':
      return 'low';
    case 'midHigh':
    case 'medium':
      return 'medium';
    case 'high':
    case 'veryHigh':
      return value;
    default:
      return null;
  }
}

export const VIDEO_COMPRESSION_PRESETS: VideoCompressionPreset[] = configuration.presets.map((preset) => {
  const id = normalizeVideoCompressionPresetId(preset.id);
  if (!id) {
    throw new Error(`Invalid video compression preset: ${preset.id}`);
  }
  return {
    ...preset,
    id,
    audioBitrateKbps: configuration.audioBitrateKbps,
    audioChannels: configuration.audioChannels === 1 ? 1 : 2,
    maxFrameRate: configuration.maxFrameRate,
  };
});

export const DEFAULT_VIDEO_COMPRESSION_PRESET_ID = normalizeVideoCompressionPresetId(configuration.defaultPresetId) ?? 'high';
export const VIDEO_COMPRESSION_MAX_FRAME_RATE = configuration.maxFrameRate;
export const VIDEO_COMPRESSION_AUDIO_BITRATE = configuration.audioBitrateKbps * 1000;

export function getVideoCompressionTargetSize(
  width: number,
  height: number,
  preset: VideoCompressionPreset,
): { width: number; height: number } {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }
  const scale = Math.min(1, preset.maxDimension / Math.max(width, height), preset.maxShortDimension / Math.min(width, height));
  return {
    width: Math.max(2, Math.floor(width * scale / 2) * 2),
    height: Math.max(2, Math.floor(height * scale / 2) * 2),
  };
}
